import { forwardRef, useId, type InputHTMLAttributes, type ReactNode } from 'react';
import { Check } from 'lucide-react';
import { cn } from './cn';

/**
 * Token-first checkbox (ADR-015 amendment 2026-07-07). Retains the native
 * `role="checkbox"` (a visually-hidden native input drives state) while presenting a
 * tokenised box + `Check` glyph and a ≥44px tappable label row. The native input is
 * the `peer`; the box and glyph are its direct siblings so `peer-checked` /
 * `peer-focus-visible` style them without JS. No raw hex/px.
 */
export interface CheckboxProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type'> {
  label: ReactNode;
  /** Optional supporting line under the label. */
  description?: ReactNode;
  /** Applied to the outer label row. */
  className?: string;
}

export const Checkbox = forwardRef<HTMLInputElement, CheckboxProps>(function Checkbox(
  { id, label, description, className, disabled, ...props },
  ref,
) {
  const generatedId = useId();
  const controlId = id ?? generatedId;

  return (
    <label
      htmlFor={controlId}
      className={cn(
        'flex min-h-11 items-start gap-3 py-1',
        disabled ? 'cursor-not-allowed opacity-60' : 'cursor-pointer',
        className,
      )}
    >
      <span className="relative mt-0.5 inline-flex h-5 w-5 shrink-0 items-center justify-center">
        <input
          ref={ref}
          id={controlId}
          type="checkbox"
          disabled={disabled}
          className="peer absolute inset-0 h-full w-full cursor-pointer opacity-0 disabled:cursor-not-allowed"
          {...props}
        />
        <span
          aria-hidden="true"
          className={cn(
            'pointer-events-none absolute inset-0 rounded-sm border border-border-strong bg-surface transition-colors ease-ds',
            'peer-checked:border-primary peer-checked:bg-primary',
            'peer-focus-visible:ring-2 peer-focus-visible:ring-ring peer-focus-visible:ring-offset-2',
          )}
        />
        <Check
          aria-hidden
          focusable={false}
          className="pointer-events-none relative h-4 w-4 text-primary-foreground opacity-0 transition-opacity ease-ds peer-checked:opacity-100"
        />
      </span>
      <span className="flex flex-col gap-0.5">
        <span className="text-sm text-text-primary">{label}</span>
        {description ? <span className="text-sm text-text-secondary">{description}</span> : null}
      </span>
    </label>
  );
});
